import React from "react";
import T from "prop-types";
import _ from "lodash";
import { Link } from "react-router-dom";
import Section from "MODULES/shared/components/Section";
import { shortDate } from "LIB/formatters/date";
import { DetailTable, Detail } from "MODULES/shared/components/DetailTable";
import {
  PartNumber,
  CompanyName,
  LocationFlag
} from "MODULES/shared/components";
import { INVENTORIES, STORE_OWNER } from "MODULES/inventories/constants";
import {
  CUSTOMER_RMA_REASONS,
  REASONS
} from "MODULES/RMAsAndWarranties/constants";
import pojo from "LIB/pojos";
import MultiLocationFeature from "MODULES/shared/components/MultiLocationFeature/MultiLocationFeature";
import { properCase } from "LIB/formatters/text";
import EditCustomerDetails from "./EditCustomerDetails";
import EditVendorDetails from "./EditVendorDetails";

export function RmaPart({ rma }) {
  if (rma.alternate) {
    if (rma.alternate.alternate_part_id) {
      return (
        <PartNumber
          part={{
            id: rma.alternate.alternate_part_id,
            part_number: rma.alternate.name
          }}
        />
      );
    }
    return <span>{rma.alternate.name}</span>;
  }

  return <PartNumber part={rma.part} />;
}

RmaPart.propTypes = {
  rma: T.shape({
    alternate: T.shape({})
  })
};

export default function Details({ rma, setForceOpenShipTo }) {
  const inventoryLink = `/inventories/${rma.inventory.uuid ||
    rma.expected_inventory_id}`;
  const refId = rma.reference_record_id;
  const customerRma = rma.type === "CustomerRma";

  let refLink = customerRma
    ? `/sales-orders/${refId}`
    : `/purchase-orders/${refId}`;

  if (rma.reference_record_line_item_type === "Lot") {
    refLink = `/lots/${refId}`;
  }

  const getReason = isCustomerRma => {
    if (isCustomerRma) {
      if (
        CUSTOMER_RMA_REASONS[rma.reason].label === "Other" &&
        rma.other_reason
      ) {
        return `${rma.other_reason}`;
      }
      return CUSTOMER_RMA_REASONS[rma.reason].label;
    }
    return REASONS[rma.reason].label;
  };

  return (
    <Section
      title={customerRma ? "Customer RMA Details" : "Vendor RMA Details"}
      modal={{
        title: customerRma ? "Edit RMA Details" : "Edit Vendor RMA Details",
        dialogClass: "modal-md",
        content: close =>
          rma.type === "VendorRma" ? (
            <EditVendorDetails
              close={close}
              initialValues={pojo(rma).pick("reason", "vendor_rma_number")}
              setForceOpenShipTo={setForceOpenShipTo}
            />
          ) : (
            <EditCustomerDetails
              initialValues={pojo(rma).pick(
                "reason",
                "vendor_rma_number",
                "other_reason"
              )}
              close={close}
            />
          )
      }}
    >
      <DetailTable>
        <tr>
          <th className="text-right">
            {customerRma ? "Customer Name" : "Vendor Name"}
          </th>
          <td>
            <CompanyName company={rma.company} />
          </td>
        </tr>
        <Detail
          label={customerRma ? "Customer Code" : "Vendor Code"}
          value={_.get(rma, "company.code")}
        />
        <Detail
          label="Contact"
          value={_.get(rma, "sales_order.contact_name")}
        />
        <Detail
          label="Contact Email"
          value={_.get(rma, "sales_order.contact_email")}
        />
        <Detail label="Part #" value={<RmaPart rma={rma} />} />
        <Detail label="Part Desc." value={rma.part.description} />
        <Detail
          label="Serial #"
          value={
            <Link to={inventoryLink}>
              {rma.sales_item
                ? rma.sales_item.serial_number
                : rma.inventory.serial_number}
            </Link>
          }
        />
        <Detail
          label="Ref #"
          value={<Link to={refLink}>{rma.reference_record_code}</Link>}
        />
        <Detail
          label="Sales Order Price Each"
          value={rma.sales_item ? rma.sales_item.price_per_unit : ""}
        />
        <Detail
          label="Customer PO #"
          value={
            rma.sales_item
              ? rma.sales_item.sales_order_customer_purchase_order_number
              : ""
          }
        />
        <MultiLocationFeature>
          <Detail
            label={properCase(STORE_OWNER)}
            value={<LocationFlag location={rma.inventory.store_owner_id} />}
          />
        </MultiLocationFeature>
        <Detail
          label="CD"
          value={
            rma.inventory.condition &&
            INVENTORIES.condition[rma.inventory.condition].label
          }
        />
        <Detail label="QTY" value={rma.quantity} />
        <Detail label="RMA Reason" value={getReason(customerRma)} />
        {!customerRma && (
          <Detail label="Discrepancy Reason" value={rma.rma_discrepancy} />
        )}
        {customerRma && (
          <Detail
            label="Expiration Date"
            value={shortDate(rma.expiration_date)}
          />
        )}
        <Detail label="Closed Date" value={shortDate(rma.closed_date)} />
        <Detail
          label="Cancelled Date"
          value={shortDate(rma.cancelled_at)}
          renderIfPresent
        />
      </DetailTable>
    </Section>
  );
}

Details.propTypes = {
  rma: T.shape({
    inventory_id: T.number.isRequired,
    reference_record_id: T.number.isRequired,
    reference_record_code: T.string.isRequired,
    expiration_date: T.string.isRequired,
    reason: T.string.isRequired,
    quantity: T.number.isRequired,
    company_name: T.string.isRequired,
    company_code: T.string.isRequired,
    part: T.shape({
      part_number: T.string.isRequired,
      description: T.string.isRequired
    }),
    inventory: T.shape({
      serial_number: T.string.isRequired,
      condition: T.string.isRequired
    }).isRequired
  })
};



// WEBPACK FOOTER //
// ./app/modules/RMAsAndWarranties/components/Detail/Panel/Details/Details.jsx